import { Component, Inject, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  HiddenToGeneralDialogComponent,
  HiddenToGeneralDialogData,
} from '../hidden-to-general-dialog/hidden-to-general-dialog.component';
import {
  AdminControlToGeneralDialogComponent,
  AdminControlToGeneralDialogData,
} from '../admin-control-to-general-dialog/admin-control-to-general-dialog.component';
import {
  PersonalizedSettingsDialogComponent,
  PersonalizedSettingsDialogData,
} from '../personalized-settings-dialog/personalized-settings-dialog.component';
import { TeamReductionDialogComponent, TeamReductionDialogData } from '../team-reduction-dialog/team-reduction-dialog.component';
import { ShiftReductionDialogComponent, ShiftReductionDialogData } from '../shift-reduction-dialog/shift-reduction-dialog.component';
import { ManagerToEmployeeDialogComponent, ManagerToEmployeeDialogData } from '../manager-to-employee-dialog/manager-to-employee-dialog.component';
import { CreateRequestComponent } from '../../Admin/help-and-support/create-request/create-request.component';
import { ModalService } from '../../Services/modal.service';
import { CreateUserComponent } from '../../Admin/create-user/create-user.component';
import {
  enrichSettingDetail,
  resolveViolationFromLabel,
  SettingViolation,
  SettingViolationRoute,
} from '../../entitlements/plan-transition-violation-catalog.util';

export interface DowngradeSettingDetail {
  code: string;
  label: string;
  fixHint: string;
  where: string;
  route?: SettingViolationRoute;
  affectedCount?: number;
}

export interface DowngradeAffectedEmployee {
  id?: number;
  name: string;
  email?: string;
  role?: string;
  details: string[];
  ssPersonalizeSettings?: boolean;
}

export interface DowngradeValidationIssue {
  code: string;
  icon: string;
  title: string;
  description: string;
  affectedEmployees?: DowngradeAffectedEmployee[];
  affectedCount?: number;
  isGlobalAdminControl?: boolean;
}

export interface DowngradeValidationDialogData {
  targetPlanName: string;
  targetPlanCode: string;
  currentPlanName: string;
  isValid: boolean;
  issues: DowngradeValidationIssue[];
  globalSettings: string[];
  globalSettingDetails?: DowngradeSettingDetail[];
  informationalSettingDetails?: DowngradeSettingDetail[];
  warningMessages: string[];
  retention?: {
    grace_period_days: number;
    cleanup_scheduled_at?: string;
    affected_date_range?: { from: string; to: string };
    target_screenshots_retention_days?: number;
    current_screenshots_retention_days?: number;
  };
}

export interface DowngradeValidationDialogResult {
  action: 'review' | 'cancel' | 'proceed' | 'refresh' | 'redirect';
}

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'app-downgrade-validation-dialog',
  templateUrl: './downgrade-validation-dialog.component.html',
  styleUrls: ['./downgrade-validation-dialog.component.css'],
  standalone: false
})
export class DowngradeValidationDialogComponent {

  /** Tracks which employee expansion sections are open (by issue index). */
  expandedSections = new Set<number>();

  /** Tracks whether the informational section (Before you downgrade) is open. Open by default. */
  isInformationalExpanded = true;

  /** Tracks whether the global settings section is open. Open by default. */
  isGlobalSettingsExpanded = true;

  /** Default success confirmation items shown when validation passes. */
  readonly successItems: { icon: string; label: string }[] = [
    { icon: 'check_circle', label: 'Teams are within plan limits' },
    { icon: 'check_circle', label: 'Shift configuration is compatible' },
    { icon: 'check_circle', label: 'Employee settings are compatible' },
    { icon: 'check_circle', label: 'Workspace settings are compatible' },
  ];

  @Output() refreshValidation = new EventEmitter<void>();

  informationalSettingDetails: SettingViolation[];
  globalSettingDetails: SettingViolation[];
  hasInformationalContent: boolean;

  constructor(
    public dialogRef: MatDialogRef<DowngradeValidationDialogComponent, DowngradeValidationDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: DowngradeValidationDialogData,
    private dialog: MatDialog,
    private router: Router,
    private modalService: ModalService
  ) {
    this.informationalSettingDetails = (this.data.informationalSettingDetails || []).map((detail) =>
      enrichSettingDetail(detail)
    );
    this.globalSettingDetails = this.data.globalSettingDetails?.length
      ? this.data.globalSettingDetails.map((detail) => enrichSettingDetail(detail))
      : (this.data.globalSettings || []).map((label) => resolveViolationFromLabel(label));
    this.hasInformationalContent =
      !!this.data.warningMessages?.length || this.informationalSettingDetails.length > 0;

    // Expand all issues by default
    if (this.data.issues?.length) {
      this.data.issues.forEach((_, i) => this.expandedSections.add(i));
    }
  }

  triggerRefresh(): void {
    this.refreshValidation.emit();
  }

  updateData(newData: DowngradeValidationDialogData): void {
    this.data = newData;
    this.informationalSettingDetails = (this.data.informationalSettingDetails || []).map((detail) =>
      enrichSettingDetail(detail)
    );
    this.globalSettingDetails = this.data.globalSettingDetails?.length
      ? this.data.globalSettingDetails.map((detail) => enrichSettingDetail(detail))
      : (this.data.globalSettings || []).map((label) => resolveViolationFromLabel(label));
    this.hasInformationalContent =
      !!this.data.warningMessages?.length || this.informationalSettingDetails.length > 0;

    this.expandedSections.clear();
    if (this.data.issues?.length) {
      this.data.issues.forEach((_, i) => this.expandedSections.add(i));
    }
  }

  isHiddenModeIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'hidden_mode' || issue.title === 'Hidden Mode';
  }

  isPersonalizedSettingsIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'personalized_settings' || issue.title === 'Personalized Employee Settings';
  }

  hasMigratableHiddenEmployees(issue: DowngradeValidationIssue): boolean {
    return (issue.affectedEmployees || []).some((employee) => !!employee.id);
  }

  isAdminControlIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'admin_control' || issue.title === 'Admin Controlled Mode';
  }

  isTeamsIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'teams' || issue.title === 'Team Management';
  }

  isShiftsIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'shifts' || issue.title === 'Shift Configuration';
  }

  isManagerRolesIssue(issue: DowngradeValidationIssue): boolean {
    return issue.code === 'manager_roles' || issue.title === 'Manager / Team Lead Roles';
  }

  isSectionExpanded(index: number): boolean {
    return this.expandedSections.has(index);
  }

  toggleSection(index: number): void {
    if (this.expandedSections.has(index)) {
      this.expandedSections.delete(index);
    } else {
      this.expandedSections.add(index);
    }
  }

  toggleInformationalSection(): void {
    this.isInformationalExpanded = !this.isInformationalExpanded;
  }

  toggleGlobalSettingsSection(): void {
    this.isGlobalSettingsExpanded = !this.isGlobalSettingsExpanded;
  }

  getEmployeeInitials(name: string): string {
    const trimmed = String(name || '').trim();
    if (!trimmed) {
      return '?';
    }
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
  }

  getEmployeeChipTooltip(employee: DowngradeAffectedEmployee): string {
    if (employee.details?.length) {
      return employee.details.join(' · ');
    }
    return employee.name;
  }

  hasMigratableAdminControlEmployees(issue: DowngradeValidationIssue): boolean {
    return (issue.affectedEmployees || []).some((employee) => !!employee.id);
  }

  // ── Dialog actions ───────────────────────────────────────────────────────

  onReviewSettings(): void {
    const ref = this.dialog.open(PersonalizedSettingsDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'personalized-settings-dialog-container',
      data: {
        mode: 'global',
        targetPlanCode: this.data.targetPlanCode,
        globalViolations: [
          ...(this.data.globalSettingDetails?.map((d) => d.label) || []),
          ...(this.data.globalSettingDetails?.map((d) => d.code) || []),
          ...(this.data.globalSettings || []),
        ],
      } as any,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }

  onUpdateGlobalAdminControl(): void {
    const ref = this.dialog.open(PersonalizedSettingsDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'personalized-settings-dialog-container',
      data: {
        mode: 'global',
        isAdminControlOnly: true,
        targetPlanCode: this.data.targetPlanCode,
        globalViolations: [
          ...(this.data.globalSettingDetails?.map((d) => d.label) || []),
          ...(this.data.globalSettingDetails?.map((d) => d.code) || []),
          ...(this.data.globalSettings || []),
        ],
      } as any,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }

  onEmployeeChipClick(emp: DowngradeAffectedEmployee, issue: DowngradeValidationIssue): void {
    if (!emp.id) return;

    if (this.isManagerRolesIssue(issue)) {
      this.navigateToEmployeeEdit(emp);
    } else if (this.isHiddenModeIssue(issue)) {
      this.onSwitchHiddenToEmployeeControlled({ ...issue, affectedEmployees: [emp] });
    } else if (this.isAdminControlIssue(issue)) {
      this.onSwitchAdminControlToEmployeeControlled({ ...issue, affectedEmployees: [emp] });
    } else if (this.isPersonalizedSettingsIssue(issue)) {
      this.onUpdatePersonalizedSettings({ ...issue, affectedEmployees: [emp] });
    }
  }

  onUpdatePersonalizedSettings(issue: DowngradeValidationIssue): void {
    const employees = (issue.affectedEmployees || []).filter((emp) => !!emp.id);
    if (!employees.length) {
      return;
    }

    const ref = this.dialog.open(PersonalizedSettingsDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'personalized-settings-dialog-container',
      data: {
        employees,
        targetPlanCode: this.data.targetPlanCode,
        globalViolations: [
          ...(this.data.globalSettingDetails?.map((d) => d.label) || []),
          ...(this.data.globalSettingDetails?.map((d) => d.code) || []),
          ...(this.data.globalSettings || []),
        ],
      } as any,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }

  navigateToEmployeeEdit(employee: DowngradeAffectedEmployee): void {
    if (employee.id) {
      if (this.modalService.isAlreadyOpenDialog(CreateUserComponent)) {
        this.modalService.closeDialogIfOpen(CreateUserComponent);
      }

      const dialogRef = this.modalService.openCommonMatDialog(
        CreateUserComponent,
        {
          className: 'create-user-modal',
          maxHeight: '80vh',
          disableClose: false,
          componentData: { isUserEditable: true, editUserId: employee.id, isRedirect: false },
        }
      );

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result && result.updated) {
          // Refresh the validation dialog state to check if the role was properly changed
          this.triggerRefresh();
        }
      });
    }
  }

  navigateToEmployees(): void {
    this.dialogRef.close({ action: 'redirect' });
    void this.router.navigate(['/employees']);
  }

  openTeamReductionDialog(): void {
    const ref = this.dialog.open(TeamReductionDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'team-reduction-dialog-container',
      data: {} satisfies TeamReductionDialogData,
    });

    ref.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        this.triggerRefresh();
      }
    });
  }

  navigateToTeams(): void {
    this.dialogRef.close({ action: 'redirect' });
    void this.router.navigate(['/teams']);
  }

  openShiftReductionDialog(): void {
    const ref = this.dialog.open(ShiftReductionDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'shift-reduction-dialog-container',
      data: {} satisfies ShiftReductionDialogData,
    });

    ref.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        this.triggerRefresh();
      }
    });
  }

  navigateToRoute(route: SettingViolationRoute): void {
    this.dialogRef.close({ action: 'redirect' });
    void this.router.navigate(route.commands, { queryParams: route.queryParams });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onProceedDowngrade(): void {
    this.dialogRef.close({ action: 'proceed' });
  }

  openSupportRequest(): void {
    this.dialog.open(CreateRequestComponent, {
      width: '600px',
      data: { type: 'support', source: 'page' }
    });
  }

  onSwitchHiddenToEmployeeControlled(issue: DowngradeValidationIssue): void {
    const employees = (issue.affectedEmployees || []).filter((emp) => !!emp.id);
    if (!employees.length) {
      return;
    }

    const ref = this.dialog.open(HiddenToGeneralDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'hidden-to-general-dialog-container',
      data: {
        employees,
      } satisfies HiddenToGeneralDialogData,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }

  onSwitchAdminControlToEmployeeControlled(issue: DowngradeValidationIssue): void {
    const employees = (issue.affectedEmployees || []).filter((emp) => !!emp.id);
    if (!employees.length) {
      return;
    }

    const ref = this.dialog.open(AdminControlToGeneralDialogComponent, {
      width: '620px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'admin-control-to-general-dialog-container',
      data: {
        employees,
      } satisfies AdminControlToGeneralDialogData,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }

  onSwitchManagersToEmployees(issue: DowngradeValidationIssue): void {
    const employees = (issue.affectedEmployees || []).filter((emp) => !!emp.id);
    if (!employees.length) {
      return;
    }

    const ref = this.dialog.open(ManagerToEmployeeDialogComponent, {
      width: '620px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'manager-to-employee-dialog-container',
      data: {
        employees,
      } satisfies ManagerToEmployeeDialogData,
    });

    ref.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.triggerRefresh();
      }
    });
  }
}
